import type { User, UserAttributes } from '@supabase/supabase-js';
import { supabase, supabaseAnonKey, supabaseUrl } from './supabase';
import { getStoredAccessToken, readSupabaseTokensFromLocalStorage } from './supabaseAuth';

function getEffectiveAccessToken(): string | null {
  return (
    getStoredAccessToken() ||
    readSupabaseTokensFromLocalStorage((import.meta as any).env?.VITE_SUPABASE_URL).accessToken
  );
}

/**
 * Fetch the current Supabase user without requiring supabase-js to have an in-memory session.
 * This is important when sign-in is handled via the API auth proxy (no refresh token on the client).
 */
export async function getSupabaseUserFromStoredAccessToken(): Promise<{
  user: User | null;
  error: Error | null;
}> {
  try {
    const accessToken = getEffectiveAccessToken();
    if (!accessToken) return { user: null, error: null };
    const { data, error } = await supabase.auth.getUser(accessToken);
    if (error) return { user: null, error };
    return { user: data.user, error: null };
  } catch (e) {
    return { user: null, error: e instanceof Error ? e : new Error('Failed to fetch user') };
  }
}

/**
 * Update Supabase user attributes using the access token directly.
 * supabase-js `updateUser` requires an in-memory session; this keeps account settings working
 * when the app is using cookie-based refresh.
 */
export async function updateSupabaseUserWithStoredAccessToken(
  attributes: UserAttributes,
): Promise<{ error: Error | null }> {
  const accessToken = getEffectiveAccessToken();
  if (!accessToken) {
    return { error: new Error('Not signed in') };
  }

  if (!supabaseUrl || !supabaseAnonKey) {
    return { error: new Error('Supabase configuration is missing') };
  }

  try {
    const response = await fetch(`${supabaseUrl}/auth/v1/user`, {
      method: 'PUT',
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(attributes),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const message =
        typeof payload?.msg === 'string'
          ? payload.msg
          : typeof payload?.message === 'string'
            ? payload.message
            : `Supabase update failed (${response.status})`;
      return { error: new Error(message) };
    }

    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e : new Error('Supabase update failed') };
  }
}
